import { useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { MetricValue } from "../../../types"
import {
  AnimatedNumber,
  NavigationDots,
  TrendBadge,
} from "~/ui-rtk/components/atoms"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"

type ScrollableMetricCardProps = {
  title: string
  metrics: MetricValue[]
  isLoading?: boolean
  className?: string
}

export default function ScrollableMetricCard({
  title,
  metrics,
  isLoading = false,
  className,
}: ScrollableMetricCardProps) {
  const navigate = useNavigate()
  const [activeIndex, setActiveIndex] = useState(0)

  const handlePrevious = useCallback(() => {
    setActiveIndex(prev => (prev === 0 ? metrics.length - 1 : prev - 1))
  }, [metrics.length])

  const handleNext = useCallback(() => {
    setActiveIndex(prev => (prev === metrics.length - 1 ? 0 : prev + 1))
  }, [metrics.length])

  const currentMetric = useMemo(
    () => metrics[activeIndex],
    [metrics, activeIndex],
  )

  const canNavigate = metrics.length > 1

  return (
    <div
      className={cn(
        "relative flex flex-col min-h-[180px] p-6 rounded-2xl",
        "bg-gradient-to-br from-system-blue-800/60 via-system-blue-800/40 to-system-blue-900/60",
        "border border-system-blue-600/30",
        "backdrop-blur-md shadow-xl",
        "transition-all duration-300",
        "hover:border-system-brand-pink-600/50 hover:shadow-2xl hover:shadow-system-brand-pink-600/10",
        "group",
        className,
      )}
    >
      {/* Background Accent */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-system-brand-pink-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Card Header - Dynamic based on current metric */}
      <div className="relative z-10 mb-4 h-[44px]">
        <h3 className="text-sm font-semibold text-system-gray-300 tracking-wide uppercase">
          {title}
        </h3>
        <p className="text-sm text-system-gray-400 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
          {currentMetric?.label}
        </p>
      </div>

      {/* Metric Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center space-y-3">
        {(() => {
          if (isLoading) {
            return (
              <div className="animate-pulse text-center w-full">
                <div className="h-10 bg-system-blue-600/30 rounded-lg w-32 mx-auto mb-3" />
                <div className="h-4 bg-system-blue-600/20 rounded w-40 mx-auto mb-2" />
                <div className="h-6 bg-system-blue-600/20 rounded-full w-20 mx-auto" />
              </div>
            )
          }
          if (currentMetric?.label === "Connect Shopify") {
            return (
              <div className="text-center w-full space-y-4">
                <div className="text-system-gray-400 text-sm">
                  Connect Shopify to view these metrics
                </div>
                <button
                  className="px-6 py-2 bg-system-brand-pink-600 hover:bg-system-brand-pink-700 text-white rounded-lg font-medium transition-colors duration-200"
                  onClick={() => navigate("/setup/connectors")}
                >
                  Connect Shopify
                </button>
              </div>
            )
          }
          return (
            <div
              key={activeIndex}
              className="animate-fadeIn text-center w-full space-y-3"
            >
              {/* Metric Value with Trend Badge */}
              <div className="relative px-4">
                <div className="text-4xl font-bold text-white tracking-tight">
                  <AnimatedNumber
                    value={currentMetric?.value ?? null}
                    format={currentMetric?.format}
                    compact={true}
                  />
                </div>

                {/* Trend Badge - Absolute Top Right */}
                {currentMetric?.trend &&
                  currentMetric?.trendValue !== undefined && (
                    <div className="">
                      <TrendBadge
                        trend={currentMetric.trend}
                        value={currentMetric.trendValue}
                      />
                    </div>
                  )}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Navigation Controls - Always render with same height */}
      <div className="relative z-10 flex items-center justify-between mt-4 pt-4 border-t border-system-blue-600/20 min-h-[40px]">
        {canNavigate ? (
          <>
            {/* Left Arrow */}
            <button
              onClick={handlePrevious}
              className={cn(
                "p-1.5 rounded-lg",
                "text-system-gray-400 hover:text-white hover:bg-system-blue-700/50",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-system-brand-pink-600/50",
              )}
              aria-label="Previous metric"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            {/* Navigation Dots */}
            <NavigationDots
              total={metrics.length}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
            />

            {/* Right Arrow */}
            <button
              onClick={handleNext}
              className={cn(
                "p-1.5 rounded-lg",
                "text-system-gray-400 hover:text-white hover:bg-system-blue-700/50",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-system-brand-pink-600/50",
              )}
              aria-label="Next metric"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </>
        ) : (
          // Single dot indicator centered when only one metric
          <div className="flex justify-center w-full">
            <div className="w-2 h-2 rounded-full bg-system-brand-pink-600" />
          </div>
        )}
      </div>
    </div>
  )
}
